import { Form } from "formik";
import PropTypes from "prop-types";
import React from "react";
import { useDispatch } from "react-redux";

import { addToast, toastMessageTypes } from "@/redux/actions/toaster";
import { Button, ButtonTypes, CustomForm } from "@/component-library";
import { CharacterCounter } from "@/component-library/CharacterCounter/CharacterCounter";
import CloseOnOutsideClick from "@/components/Common/CloseOnOutsideClick/CloseOnOutsideClick";
import { convertNumberToCurrency } from "@/utils/typeUtils";
import { FormCurrencyField } from "@/component-library/FormFields/FormCurrencyField/FormCurrencyField";
import { FormFieldError } from "@/component-library/FormFields/FormFieldError/FormFieldError";
import { FormTextArea } from "@/component-library/FormFields/FormTextArea/FormTextArea";
import { paymentRequests } from "@/api/(requests)/payment";

import {
  COMMENT_CHAR_LIMIT,
  PAYMENT_MODAL_INITIAL_VALUES,
  PAYMENT_MODAL_VALIDATION,
} from "./PaymentModalSchema";

import "./styles.scss";

export const PaymentModal = ({ isOpen, patientId, setIsOpen, updateTransactionHistory }) => {
  const dispatch = useDispatch();

  const preventEnterFormSubmission = (event) => {
    if (event.key === "Enter" || event.code === "Enter") {
      event.preventDefault();
    }
  };

  const getTitle = (amount) => {
    const amountNumber = Number(amount);

    if (isNaN(amountNumber) || amount === "") {
      return "New";
    }

    return `$${convertNumberToCurrency(amountNumber)}`;
  };

  const getChargeAmount = (amount) => {
    const amountNumber = Number(amount);

    if (isNaN(amountNumber) || amount === "") {
      return "";
    }

    return ` $${convertNumberToCurrency(amountNumber)}`;
  };

  const renderForm = (formikProps) => {
    const amount = formikProps.values["amount"];

    return (
      <CloseOnOutsideClick
        className="PaymentModal-content"
        setShowComponent={(value) => {
          formikProps.resetForm(formikProps);
          setIsOpen(value);
        }}
      >
        <Form noValidate={true} onKeyDown={preventEnterFormSubmission}>
          <div className="PaymentModal-form">
            <h1 className="PaymentModal-title" data-public>
              {getTitle(amount)} Payment
            </h1>
            <FormCurrencyField
              className="PaymentModal-amount_input"
              formikProps={formikProps}
              name="amount"
              title="Amount"
            />
            <FormTextArea
              className="PaymentModal-comment_input"
              customError={(value, errorMessage, isTouched) => {
                return (
                  <div className="PaymentModal-comment_input-feedback_container">
                    {errorMessage && isTouched && <FormFieldError>{errorMessage}</FormFieldError>}
                    <CharacterCounter
                      characterLimit={COMMENT_CHAR_LIMIT}
                      className="PaymentModal-comment_input-character_counter"
                      length={value?.length}
                    />
                  </div>
                );
              }}
              formikProps={formikProps}
              name="comment"
              title={
                <>
                  Comment <p className="PaymentModal-optional_text">(Optional)</p>
                </>
              }
            />
            <div className="PaymentModal-button_wrapper" data-public>
              <Button
                buttonType={ButtonTypes.primaryOutlineV2}
                className="PaymentModal-button PaymentModal-cancel_button"
                onClick={() => {
                  formikProps.resetForm(formikProps);
                  setIsOpen(false);
                }}
              >
                Cancel
              </Button>
              <Button
                buttonType={ButtonTypes.primaryV2}
                className="PaymentModal-button PaymentModal-submit_button"
                style={{ flexGrow: 1 }}
                type="submit"
              >
                Charge{getChargeAmount(amount)}
              </Button>
            </div>
          </div>
        </Form>
      </CloseOnOutsideClick>
    );
  };

  return (
    <>
      {isOpen && (
        <div className="PaymentModal-container">
          <CustomForm
            initialValues={PAYMENT_MODAL_INITIAL_VALUES}
            onSubmit={async (formData) => {
              // transform string to number
              const amount = Number(formData["amount"]);

              const data = {
                ...formData,
                amount,
                patient_id: patientId,
              };

              try {
                const result = await paymentRequests.manualCharge({
                  data,
                });

                // Refetch data only if payment succeeds.
                if (result) {
                  updateTransactionHistory();
                }

                dispatch(
                  addToast({
                    message: result.message,
                    messageType: toastMessageTypes.success_v2,
                  }),
                );
              } finally {
                setIsOpen(false);
              }
            }}
            render={renderForm}
            validateOnBlur={true}
            validateOnChange={true}
            validationSchema={PAYMENT_MODAL_VALIDATION}
          />
        </div>
      )}
    </>
  );
};

PaymentModal.propTypes = {
  isOpen: PropTypes.bool,
  patientId: PropTypes.string,
  setIsOpen: PropTypes.func,
  updateTransactionHistory: PropTypes.func,
};
